/**
 * Signing the drafts a create just made, in place, one after another: the
 * Request access modal's basket panel, instead of a trip to the signing page.
 *
 * Each draft gets its own challenge and its own passkey prompt, because a
 * signature covers one request. The ceremony itself is the signing page's
 * (`signChallenge`); this decides only what comes next after each answer.
 *
 * The prompt opens by itself once a challenge arrives. Safari (and a page
 * without focus anywhere) refuses a prompt that no recent click asked for,
 * and the submit click's activation can lapse over the create and the mint.
 * Browsers name that refusal no differently from a closed prompt
 * (NotAllowedError), so it is told apart by timing: a prompt nobody clicked
 * for, refused before a person could have answered it, asks for a click
 * ("Sign with passkey"); anything else is a prompt the person closed ("Try
 * again"). Either way the click opens the prompt before anything is awaited,
 * so the browser sees it inside the click.
 *
 * A member with no passkey yet is handed to the signing page, which can add
 * one (and asks them to sign in again first when it must). So is one who asks
 * to add a passkey on this device ("Add one here"), wherever a prompt did not
 * go through: the browser will not say whether the person closed it or this
 * device has no passkey for them.
 *
 * Once the last draft is signed the run shows "Sent" for a moment, then
 * reports done (closing it early just reports done sooner).
 *
 * The browser, the network and the clock are passed in, so every branch is
 * unit tested without them.
 */
import type { StepUpChallenge } from "state/members/stepUp";
import {
  failureMessage,
  START_SIGNING_FAILED_MESSAGE,
} from "state/members/stepUpFailure";

import { hasExpired, msUntil } from "./clock";
import { draftHandoff, type StepUpLocationState } from "./navigation";
import { signChallenge, type SignDeps, type SignOutcome } from "./signing";

/** Which draft of how many: "Signing 2 of 3". `index` counts from zero. */
export type DraftStep = { index: number; total: number };

export type DraftProblem = "expired" | "used" | "failed" | "unsupported";

export type DraftSigningState =
  | { name: "idle" }
  /** Asking the server for this draft's challenge. */
  | { name: "preparing"; step: DraftStep }
  /** The passkey prompt is open. */
  | { name: "signing"; step: DraftStep; challenge: StepUpChallenge }
  /**
   * Waiting for a click. `gesture`: the browser would not open the prompt
   * without one. `closed`: the prompt closed without signing.
   */
  | {
      name: "ready";
      step: DraftStep;
      challenge: StepUpChallenge;
      reason: "gesture" | "closed";
    }
  /**
   * This draft could not be signed. Try again asks for a fresh challenge,
   * except in a browser that cannot use passkeys at all (`unsupported`).
   * `challenge` is null when none could be had. `enrolHere`: what failed was
   * the hand-off to add a passkey here, so Try again asks for that again.
   */
  | {
      name: "problem";
      step: DraftStep;
      challenge: StepUpChallenge | null;
      problem: DraftProblem;
      message: string;
      enrolHere?: true;
    }
  /** Every draft is signed; shown for a moment before the run reports done. */
  | { name: "sent"; step: DraftStep; signed: number };

/**
 * How a run ended. `unsigned` drafts stay in My Access with their Sign
 * button. `handoff`: the challenge to open on the signing page, and whether
 * the page adds a passkey on this device first (`enrolHere`, for a member
 * whose passkeys are on other devices).
 */
export type DraftSigningResult = {
  signed: number;
  unsigned: number;
  handoff: { challenge: StepUpChallenge; enrolHere: boolean } | null;
};

/**
 * Whether "Add one here" is on offer: wherever a prompt did not go through
 * and could be tried again. Not while waiting, not once sent, and not in a
 * browser that cannot use passkeys at all.
 */
export const canAddHere = (
  state: DraftSigningState,
): state is Extract<DraftSigningState, { name: "ready" | "problem" }> =>
  state.name === "ready" ||
  (state.name === "problem" && state.problem !== "unsupported");

/**
 * Where a run that ended in a hand-off goes: the signing page, back to where
 * the person was once signed, or to My Access while other drafts wait there.
 */
export const handoffNavigation = (
  { unsigned, handoff }: DraftSigningResult,
  here: string,
): { to: string; state: StepUpLocationState } | null =>
  handoff
    ? draftHandoff(handoff.challenge.id, here, unsigned - 1, {
        enrolHere: handoff.enrolHere,
      })
    : null;

export type DraftSignerDeps = Omit<SignDeps, "getAssertion"> & {
  /** The browser's prompt; `signal` closes it (webauthn's getAssertion). */
  getAssertion: (
    options: PublicKeyCredentialRequestOptions,
    signal?: AbortSignal,
  ) => ReturnType<SignDeps["getAssertion"]>;
  /** Mints the challenge to sign one draft (startRequestSignature). */
  mint: (requestId: string) => Promise<StepUpChallenge>;
  /** A signature on this challenge landed: refresh whatever it changed. */
  onSigned: (challengeId: string) => void;
  /** Whether this browser can run a passkey prompt at all. */
  canSign: () => boolean;
  now?: () => number;
  /** Runs `run` after `ms` (setTimeout); passed in so tests need no timers. */
  after?: (ms: number, run: () => void) => void;
};

export type DraftSignerEvents = {
  onChange: (state: DraftSigningState) => void;
  onDone: (result: DraftSigningResult) => void;
};

/**
 * A refusal faster than this, for a prompt nobody clicked for, is the
 * browser's and not a person's: nobody reads a passkey prompt and closes it
 * within a second.
 */
export const QUICK_REFUSAL_MS = 1_000;

/** How long "Sent" shows before the run reports done. */
export const SENT_PAUSE_MS = 1_200;

const IDLE: DraftSigningState = { name: "idle" };

export const createDraftSigner = (
  deps: DraftSignerDeps,
  events: DraftSignerEvents,
) => {
  const now = deps.now ?? Date.now;
  const after =
    deps.after ?? ((ms: number, run: () => void) => void window.setTimeout(run, ms));
  let state: DraftSigningState = IDLE;
  let drafts: string[] = [];
  let signed = 0;
  // Bumped whenever a run starts or stops, so an answer that lands for an
  // earlier run (a prompt still open after Cancel) changes nothing here.
  let run = 0;
  // When the current challenge arrived. The server mints it pending, so if it
  // already looked expired then, this browser's clock is wrong and is not
  // used to time it out (the signing page's rule).
  let receivedAt = 0;
  // The open prompt's off switch. Some browsers leave the page clickable
  // while their prompt is up, so a run that stops closes it: a prompt left
  // open would still sign a request the run just reported unsigned.
  let prompt: AbortController | null = null;

  const closePrompt = () => {
    prompt?.abort();
    prompt = null;
  };

  const set = (next: DraftSigningState) => {
    state = next;
    events.onChange(next);
  };

  const stepAt = (index: number): DraftStep => ({ index, total: drafts.length });

  const finish = (handoff: DraftSigningResult["handoff"] = null) => {
    run += 1;
    closePrompt();
    const result = { signed, unsigned: drafts.length - signed, handoff };
    set(IDLE);
    events.onDone(result);
  };

  const settle = (
    outcome: SignOutcome,
    index: number,
    challenge: StepUpChallenge,
    quickRefusal: boolean,
  ) => {
    const step = stepAt(index);
    switch (outcome.kind) {
      case "signed": {
        signed += 1;
        if (index + 1 < drafts.length) {
          void prepare(index + 1);
          return;
        }
        const mine = run;
        set({ name: "sent", step, signed });
        after(SENT_PAUSE_MS, () => {
          if (mine === run) finish();
        });
        return;
      }
      case "cancelled":
        set({ name: "ready", step, challenge, reason: "closed" });
        return;
      case "not_allowed":
        set({
          name: "ready",
          step,
          challenge,
          reason: quickRefusal ? "gesture" : "closed",
        });
        return;
      case "failed":
        // A SecurityError before any prompt reads the same as a missing click.
        if (quickRefusal) {
          set({ name: "ready", step, challenge, reason: "gesture" });
          return;
        }
        set({ name: "problem", step, challenge, problem: "failed", message: outcome.message });
        return;
      case "expired":
        set({ name: "problem", step, challenge, problem: "expired", message: "" });
        return;
      case "used":
        set({ name: "problem", step, challenge, problem: "used", message: outcome.message });
    }
  };

  // Synchronous up to the browser call, so a click that leads here opens the
  // prompt inside its own activation.
  const ceremony = (index: number, challenge: StepUpChallenge, auto: boolean) => {
    const mine = run;
    const startedAt = now();
    const clockTrusted = !hasExpired(challenge.expiresAt, receivedAt);
    if (clockTrusted && hasExpired(challenge.expiresAt, startedAt)) {
      set({ name: "problem", step: stepAt(index), challenge, problem: "expired", message: "" });
      return;
    }

    set({ name: "signing", step: stepAt(index), challenge });
    let browserRefused = false;
    const controller = new AbortController();
    prompt = controller;
    void signChallenge(
      challenge,
      {
        timeoutMs: clockTrusted ? msUntil(challenge.expiresAt, startedAt) : undefined,
      },
      {
        ...deps,
        getAssertion: async (options) => {
          try {
            return await deps.getAssertion(options, controller.signal);
          } catch (error) {
            browserRefused = true;
            throw error;
          }
        },
      },
    ).then((outcome) => {
      // A signature stands whatever happened here since.
      if (outcome.kind === "signed") deps.onSigned(challenge.id);
      if (mine !== run) return;
      const quickRefusal =
        auto && browserRefused && now() - startedAt < QUICK_REFUSAL_MS;
      settle(outcome, index, challenge, quickRefusal);
    });
  };

  // `enrolHere`: the challenge is for the signing page, which adds a passkey
  // on this device first.
  const prepare = async (index: number, enrolHere = false) => {
    const mine = run;
    set({ name: "preparing", step: stepAt(index) });
    let challenge: StepUpChallenge;
    try {
      challenge = await deps.mint(drafts[index]);
    } catch (error) {
      if (mine !== run) return;
      set({
        name: "problem",
        step: stepAt(index),
        challenge: null,
        problem: "failed",
        message: failureMessage(error, START_SIGNING_FAILED_MESSAGE),
        ...(enrolHere ? { enrolHere: true as const } : {}),
      });
      return;
    }
    if (mine !== run) return;
    receivedAt = now();
    if (enrolHere || !challenge.hasPasskeys) {
      finish({ challenge, enrolHere });
      return;
    }
    ceremony(index, challenge, true);
  };

  return {
    getState: () => state,

    /** Signs these drafts in order. False, and nothing happens, for none. */
    start: (draftIds: string[]): boolean => {
      if (draftIds.length === 0) return false;
      run += 1;
      drafts = [...draftIds];
      signed = 0;
      if (!deps.canSign()) {
        set({
          name: "problem",
          step: stepAt(0),
          challenge: null,
          problem: "unsupported",
          message: "",
        });
        return true;
      }
      void prepare(0);
      return true;
    },

    /** The click: opens the prompt for the challenge in hand. */
    sign: () => {
      if (state.name !== "ready") return;
      ceremony(state.step.index, state.challenge, false);
    },

    /**
     * Try again after a problem: a fresh challenge, then the prompt, or the
     * hand-off again when that is what could not start.
     */
    retry: () => {
      if (state.name !== "problem" || state.problem === "unsupported") return;
      void prepare(state.step.index, state.enrolHere === true);
    },

    /**
     * "Add one here": a fresh challenge for this draft (the page gets its
     * whole life for adding a passkey and signing), handed to the signing
     * page to add a passkey on this device first.
     */
    addHere: () => {
      if (!canAddHere(state)) return;
      void prepare(state.step.index, true);
    },

    /**
     * Cancel: what is not signed yet stays a draft in My Access. True when a
     * run was stopped (and `onDone` has heard so), false when none was on.
     */
    stop: (): boolean => {
      if (state.name === "idle") return false;
      finish();
      return true;
    },

    /** Put away without a word, as when the modal goes. */
    dispose: () => {
      run += 1;
      closePrompt();
      state = IDLE;
    },
  };
};
